import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import { withTypedAssignees } from '@/api/queries/tasks/assigneeFilter';
import { graphql } from '@/gql';
import { GetTasksInput, PaginationInput } from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';

const GetProjectTasks = graphql(`
  query ProjectTasks(
    $projectId: ID!
    $pagination: PaginationInput!
    $input: GetTasksInput
  ) {
    projectById(id: $projectId) {
      tasks(pagination: $pagination, input: $input) {
        items {
          __typename
          ... on IEntity {
            id
          }
          createdBy {
            id
          }
          name
          deadline
          completionStatus
          assignedTo {
            __typename
            ... on User {
              id
              firstName
              lastName
              fullName
              colour
              email
              avatarUrl
            }
            ... on Team {
              id
              name
            }
          }
          events {
            id
            name
          }
          ... on ChecklistTask {
            name
            completedAt
            checklistItems {
              id
              publicId
              completedAt
            }
          }
          ... on FormTask {
            name
            completedAt
            formRevisions {
              id
            }
          }
          ... on LegacyFormTask {
            name
            completedAt
            formRevisions {
              id
            }
          }
        }
        totalCount
      }
    }
  }
`);

const useGetProjectTasks = (
  projectId?: string,
  input?: GetTasksInput,
  pagination?: PaginationInput
) => {
  const { data, ...rest } = useQuery({
    enabled: !!projectId,
    // Without this the failure is silent, and callers that count Tasks render
    // an empty list — indistinguishable from having none.
    meta: { errorMessage: 'Failed to load tasks' },
    queryKey: projectKeys.tasks(projectId!, input, pagination),
    queryFn: () => {
      return gqlClient.request(GetProjectTasks, {
        projectId: projectId!,
        input: withTypedAssignees(input),
        pagination: pagination || PAGINATION_ARG_DEFAULTS,
      });
    },
  });

  return { tasks: data?.projectById.tasks, data, ...rest };
};

export default useGetProjectTasks;
